import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { useContent } from "@/lib/useContent";

const LOGO_URL =
    "https://customer-assets-7cd3h4nn.emergentagent.net/job_7890e65a-8a22-475d-8f20-42607090552b/artifacts/0cc171a306344fa8_IMG_9558.jpg";
const SAGAR_PHOTO_URL =
    "https://customer-assets-39nsmqrw.emergentagent.net/job_bomgay-stories/artifacts/3a7f3673056d391b_IMG_9631.jpg";

const PILLARS = [
    {
        n: "01",
        title: "Memory",
        body: "Queer stories deserve to be remembered. We document lived experiences, preserve personal histories and make room for voices that have too often been left out of the record.",
    },
    {
        n: "02",
        title: "Curiosity",
        body: "Identity, love, sex, politics, class, caste, activism, desire and belonging are all part of queer life. We approach difficult questions with openness, care and respect for the people sharing their stories.",
    },
    {
        n: "03",
        title: "Community",
        body: "Queer life is not one story. It is a constellation of voices, generations, identities and experiences. We make room for complexity, contradiction and meaningful conversation.",
    },
];

export default function About() {
    const { settings } = useContent();

    return (
        <div data-testid="about-page">
            <section className="bg-paper border-b-2 border-ink">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-20">
                    <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                        About the podcast
                    </p>
                    <h1
                        data-testid="about-heading"
                        className="mt-5 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-4xl"
                    >
                        <MaskedLine delay={0.1}>A queer cultural record,</MaskedLine>
                        <MaskedLine delay={0.25} className="italic text-pink">
                            made in Bombay.
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.4}>
                        <div className="mt-8 max-w-2xl space-y-5 text-base sm:text-lg leading-relaxed text-ink/80">
                            <p>
                                BOMGAY documents the stories, histories and lived experiences
                                that shape LGBTQIA+ life across India, South Asia and beyond.
                            </p>
                            <p>
                                Through intimate conversations, oral histories and cultural
                                inquiry, we explore the everyday realities of queer life. The
                                people we love, the spaces we inhabit, the communities we
                                build, the freedoms we fight for and the memories we carry.
                            </p>
                            <p>
                                We believe queer history is more than a timeline of legal
                                victories or public milestones. It is also found in personal
                                recollections, forgotten meeting places, family conversations,
                                friendships, desire, resistance and ordinary acts of survival.
                            </p>
                            <p>
                                BOMGAY brings these stories into the record, one voice and
                                one conversation at a time.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <Marquee dark />

            {/* Host spotlight */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-5">
                        <div className="relative rotate-2">
                            <img
                                src={SAGAR_PHOTO_URL}
                                alt="Sagar Malkar speaking on stage with a microphone"
                                data-testid="host-photo"
                                className="w-full border-2 border-ink hard-shadow object-cover object-top aspect-[4/5]"
                            />
                            <img
                                src={LOGO_URL}
                                alt="BOMGAY logo"
                                className="absolute -bottom-6 -left-4 w-24 sm:w-28 -rotate-6 border-2 border-ink hard-shadow-sm bg-cream"
                            />
                            <span className="absolute -top-4 -right-3 rotate-6 bg-pink text-cream font-sub font-bold text-xs px-3 py-2 border-2 border-ink hard-shadow-sm">
                                Your host
                            </span>
                        </div>
                    </Reveal>
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                                Your host
                            </p>
                            <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">
                                Meet Sagar Malkar
                            </h2>
                            <p className="mt-2 font-sub italic text-ink/70">
                                Filmmaker, media entrepreneur, law student and storyteller, based in Mumbai
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-6 max-w-2xl space-y-4 text-base leading-relaxed text-ink/80">
                                <p data-testid="host-bio-1">{settings.host_bio_1}</p>
                                <p data-testid="host-bio-2">{settings.host_bio_2}</p>
                                <p data-testid="host-bio-3">{settings.host_bio_3}</p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <Link
                                to="/episodes"
                                data-testid="host-conversations-link"
                                className="mt-8 inline-flex items-center gap-2 bg-pink text-cream font-sub font-bold px-7 py-4 border-2 border-ink hard-shadow transition-transform duration-200 hover:-translate-y-1"
                            >
                                Explore the Conversations <ArrowRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Principles */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 sm:pt-32">
                <Reveal>
                    <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                        The manifesto
                    </p>
                    <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">
                        What BOMGAY stands for
                    </h2>
                </Reveal>
                <div className="mt-10 grid md:grid-cols-3 gap-8">
                    {PILLARS.map((p, i) => (
                        <Reveal key={p.n} delay={i * 0.1}>
                            <div
                                data-testid={`pillar-${p.n}`}
                                className={`h-full bg-cream border-2 border-ink hard-shadow-sm p-7 ${
                                    i % 2 ? "rotate-1" : "-rotate-1"
                                } transition-transform duration-300 hover:rotate-0`}
                            >
                                <p className="font-mono-brand text-xs tracking-[0.2em] text-mustard">
                                    {p.n}
                                </p>
                                <h3 className="mt-3 font-sub font-black text-xl text-ink">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-ink/80">
                                    {p.body}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <Link
                        to="/episodes"
                        data-testid="about-episodes-link"
                        className="mt-12 inline-flex items-center gap-2 bg-pink text-cream font-sub font-bold px-7 py-4 border-2 border-ink hard-shadow transition-transform duration-200 hover:-translate-y-1"
                    >
                        Hear the Stories <ArrowRight className="w-4 h-4" />
                    </Link>
                </Reveal>
            </section>
        </div>
    );
}
